import {
  CustomFieldConditionOperator,
  operatorCarriesValue,
  PREDICATE_TYPENAME_TO_OPERATOR,
} from '@payaca/gql-helpers';
import { assertUnreachable } from '@payaca/utilities/guards';

import {
  CustomField,
  PipelineGuardAnd as GqlPipelineGuardAnd,
  PipelineGuardCustomFieldCondition as GqlPipelineGuardCustomFieldCondition,
  PipelineGuardNot as GqlPipelineGuardNot,
  PipelineGuardOr as GqlPipelineGuardOr,
  PipelineGuardSimpleCondition as GqlPipelineGuardSimpleCondition,
  PipelineGuardInput,
  PipelineGuardSimpleConditionType,
} from '@/gql/graphql';
import {
  GuardValidity,
  PipelineGuardAnd,
  PipelineGuardAndChild,
  PipelineGuardProjectHasTag,
  PipelineGuardSimple,
} from '@/ui/pages/pipelineConfigPage/types/pipelineGuardTypes';
import { v4 as uuid } from 'uuid';

export type PipelineGuardOrFormState = {
  type: 'or';
  children: PipelineGuardAnd[];
};

type RequiredTypename<T> = T & {
  __typename: NonNullable<T extends { __typename?: infer U } ? U : never>;
};

/** Narrow tag shape matching what our queries actually select */
type GraphQLTagGuardCondition = {
  __typename: 'PipelineGuardProjectHasTagCondition';
  validityResult: { isValid: boolean; reasons: string[] };
  tag: { publicId: string; name: string; colour: string | null };
};

/** Narrow CFC shape matching what our queries actually select. */
type GraphQLCustomFieldGuardCondition = {
  __typename: 'PipelineGuardCustomFieldCondition';
  validityResult: { isValid: boolean; reasons: string[] };
  fieldPath: string;
  predicate: GraphQLCustomFieldConditionPredicate;
  field: Pick<CustomField, 'id' | 'label' | 'type' | 'identifier'> | null;
};

type GraphQLComparisonEntity = {
  __typename: 'User';
  publicId: string;
  fullName: string;
} | null;

type GraphQLCustomFieldConditionPredicate =
  | {
      __typename: 'EqualsCustomFieldConditionPredicate';
      comparisonValue: unknown;
      comparisonEntity: GraphQLComparisonEntity;
    }
  | {
      __typename: 'NotEqualsCustomFieldConditionPredicate';
      comparisonValue: unknown;
      comparisonEntity: GraphQLComparisonEntity;
    }
  | { __typename: 'IsSetCustomFieldConditionPredicate' }
  | { __typename: 'IsNotSetCustomFieldConditionPredicate' }
  | { __typename: 'AllChildrenAreSetCustomFieldConditionPredicate' }
  | { __typename: 'AllChildrenAreNotSetCustomFieldConditionPredicate' }
  | { __typename: 'AnyChildIsSetCustomFieldConditionPredicate' };

const ALWAYS_VALID = { isValid: true as const, reasons: [] as string[] };

type GraphQLGuardAndChild =
  | RequiredTypename<GqlPipelineGuardSimpleCondition>
  | GraphQLTagGuardCondition
  | GraphQLCustomFieldGuardCondition
  | (Omit<RequiredTypename<GqlPipelineGuardNot>, 'child'> & {
      child:
        | RequiredTypename<GqlPipelineGuardSimpleCondition>
        | GraphQLTagGuardCondition;
    });

export type GraphQLGuardOr = Omit<
  RequiredTypename<GqlPipelineGuardOr>,
  'children'
> & {
  children: Array<
    Omit<RequiredTypename<GqlPipelineGuardAnd>, 'children'> & {
      children: Array<GraphQLGuardAndChild>;
    }
  >;
};

type GraphQLGuardOrLoose = GqlPipelineGuardOr;

function toValidity(
  vr: { isValid: boolean; reasons: string[] } | null | undefined
): GuardValidity | undefined {
  if (!vr) return undefined;
  return { isValid: vr.isValid, reasons: vr.reasons };
}

/**
 * Read-boundary helper: maps a `CustomFieldConditionPredicate` GraphQL
 * union member's `__typename` to its operator literal, narrowing through
 * `PREDICATE_TYPENAME_TO_OPERATOR` so the form state's discriminated
 * union is reconstructed faithfully.
 */
function predicateTypenameToOperator(
  typename: GraphQLCustomFieldConditionPredicate['__typename'] | undefined
): CustomFieldConditionOperator {
  if (!typename) {
    throw new Error(
      'CustomFieldConditionPredicate is missing __typename — query must select __typename on every union member'
    );
  }
  return PREDICATE_TYPENAME_TO_OPERATOR[typename];
}

/**
 * Inspect the @oneOf input's set key to recover the operator and any
 * carried comparison value. Mirrors `predicateTypenameToOperator` for the
 * optimistic-response code path, which doesn't have predicate
 * `__typename`s yet.
 */
function extractPredicateInput(
  predicate: NonNullable<
    NonNullable<
      NonNullable<PipelineGuardInput['customFieldCondition']>
    >['predicate']
  >
): {
  operator: CustomFieldConditionOperator;
  comparisonValue?: unknown;
} {
  if (predicate.equals)
    return {
      operator: 'equals',
      comparisonValue: predicate.equals.comparisonValue,
    };
  if (predicate.notEquals)
    return {
      operator: 'notEquals',
      comparisonValue: predicate.notEquals.comparisonValue,
    };
  if (predicate.isSet) return { operator: 'isSet' };
  if (predicate.isNotSet) return { operator: 'isNotSet' };
  if (predicate.allChildrenAreSet) return { operator: 'allChildrenAreSet' };
  if (predicate.allChildrenAreNotSet)
    return { operator: 'allChildrenAreNotSet' };
  if (predicate.anyChildIsSet) return { operator: 'anyChildIsSet' };
  throw new Error(
    'CustomFieldConditionPredicateInput must specify exactly one operator'
  );
}

/**
 * Build a typed predicate-union object for the optimistic response so
 * downstream selectors (which discriminate on `predicate.__typename`)
 * see a faithfully-shaped CFC condition.
 */
function buildPredicateResponse(
  operator: CustomFieldConditionOperator,
  comparisonValue: unknown
): GraphQLCustomFieldConditionPredicate {
  switch (operator) {
    case 'equals':
      return {
        __typename: 'EqualsCustomFieldConditionPredicate',
        comparisonValue,
        comparisonEntity: null,
      };
    case 'notEquals':
      return {
        __typename: 'NotEqualsCustomFieldConditionPredicate',
        comparisonValue,
        comparisonEntity: null,
      };
    case 'isSet':
      return { __typename: 'IsSetCustomFieldConditionPredicate' };
    case 'isNotSet':
      return { __typename: 'IsNotSetCustomFieldConditionPredicate' };
    case 'allChildrenAreSet':
      return { __typename: 'AllChildrenAreSetCustomFieldConditionPredicate' };
    case 'allChildrenAreNotSet':
      return {
        __typename: 'AllChildrenAreNotSetCustomFieldConditionPredicate',
      };
    case 'anyChildIsSet':
      return { __typename: 'AnyChildIsSetCustomFieldConditionPredicate' };
    default:
      return assertUnreachable(operator);
  }
}

/**
 * Build the @oneOf `CustomFieldConditionPredicateInput` shape for the
 * mutation's input. The key matches the operator literal directly (the
 * @oneOf's keys are the operator names), so no translation table is
 * needed on the write side.
 */
function operatorToPredicateInput(
  operator: CustomFieldConditionOperator,
  comparisonValue: unknown
): NonNullable<
  NonNullable<
    NonNullable<PipelineGuardInput['customFieldCondition']>
  >['predicate']
> {
  switch (operator) {
    case 'equals':
      return { equals: { comparisonValue } };
    case 'notEquals':
      return { notEquals: { comparisonValue } };
    case 'isSet':
      return { isSet: {} };
    case 'isNotSet':
      return { isNotSet: {} };
    case 'allChildrenAreSet':
      return { allChildrenAreSet: {} };
    case 'allChildrenAreNotSet':
      return { allChildrenAreNotSet: {} };
    case 'anyChildIsSet':
      return { anyChildIsSet: {} };
    default:
      return assertUnreachable(operator);
  }
}

/**
 * Two children are "the same logical row" if their discriminator and
 * identity-defining fields agree. Used as the tie-breaker when matching
 * by content during inserts/removes, where position alone is unreliable.
 */
function childIdentitiesMatch(
  a: PipelineGuardAndChild,
  b: PipelineGuardAndChild
): boolean {
  if (a.type !== b.type) return false;
  if (a.type === 'simple' && b.type === 'simple') {
    return a.conditionType === b.conditionType;
  }
  if (a.type === 'projectHasTag' && b.type === 'projectHasTag') {
    return a.tagPublicId === b.tagPublicId;
  }
  if (a.type === 'customFieldCondition' && b.type === 'customFieldCondition') {
    return a.fieldPath === b.fieldPath;
  }
  if (a.type === 'not' && b.type === 'not') {
    if (a.child.type !== b.child.type) return false;
    if (a.child.type === 'simple' && b.child.type === 'simple') {
      return a.child.conditionType === b.child.conditionType;
    }
    if (a.child.type === 'projectHasTag' && b.child.type === 'projectHasTag') {
      return a.child.tagPublicId === b.child.tagPublicId;
    }
    return false;
  }
  return false;
}

/**
 * Pair each new child with the previous child whose identifier should
 * be carried forward, so React keys stay stable across re-derivations.
 *
 * - Equal counts: pair by position. This is the typing flow — the user
 *   is editing one row in place, and the row's discriminator may have
 *   just changed mid-session (e.g. `simple → customFieldCondition`).
 *   Reusing the position's identifier across that change keeps
 *   `GuardConditionRow` mounted so the focused input isn't torn off.
 * - Differing counts: pair by content (greedy). Distinguishes a remove
 *   from an in-place edit and avoids cascading remounts of unaffected
 *   siblings.
 *
 * Returns the previous tempIdentifier per new index, or `null` when no
 * pairing was made (caller mints a fresh uuid).
 */
function pairChildIdentifiers(
  newChildren: PipelineGuardAndChild[],
  previousChildren: PipelineGuardAndChild[] | undefined
): Array<string | null> {
  if (!previousChildren || previousChildren.length === 0) {
    return newChildren.map(() => null);
  }
  if (newChildren.length === previousChildren.length) {
    return newChildren.map(
      (_, index) => previousChildren[index].tempIdentifier
    );
  }
  const usedPrev = new Set<number>();
  return newChildren.map((newChild) => {
    for (let i = 0; i < previousChildren.length; i++) {
      if (usedPrev.has(i)) continue;
      if (childIdentitiesMatch(newChild, previousChildren[i])) {
        usedPrev.add(i);
        return previousChildren[i].tempIdentifier;
      }
    }
    return null;
  });
}

/**
 * When position-paired the previous row at the same slot was a `not`,
 * carry its inner child's tempIdentifier onto the new inner. Used so a
 * `not > simple` row keeps its inner key stable across edits.
 */
function pairInnerNotIdentifier(
  previous: PipelineGuardAndChild | undefined
): string | null {
  if (!previous || previous.type !== 'not') return null;
  return previous.child.tempIdentifier;
}

export function graphqlGuardToFormState(
  guard:
    | { __typename: 'PipelineGuardOr'; children: Array<unknown> }
    | {}
    | null
    | undefined,
  previous?: PipelineGuardOrFormState | null
): PipelineGuardOrFormState | null {
  if (!guard || !('__typename' in guard)) {
    return null;
  }

  if (guard.__typename !== 'PipelineGuardOr') {
    return null;
  }

  const orGuard = guard as GraphQLGuardOrLoose;

  const andGroups: PipelineGuardAnd[] = orGuard.children
    .map((andChild, andIndex) => {
      if (!andChild || !('__typename' in andChild)) {
        return null;
      }

      if (andChild.__typename !== 'PipelineGuardAnd' || !andChild.children) {
        return null;
      }

      const previousAndGroup = previous?.children[andIndex];

      // First pass: build new children with placeholder identifiers, so we
      // can compute pair-wise matches against the previous group's
      // children before assigning ids.
      const PLACEHOLDER = '';
      const builtChildren: PipelineGuardAndChild[] = andChild.children
        .map((child): PipelineGuardAndChild | null => {
          if (!child || !('__typename' in child)) {
            return null;
          }

          if (child.__typename === 'PipelineGuardSimpleCondition') {
            return {
              type: 'simple',
              conditionType: child.type,
              tempIdentifier: PLACEHOLDER,
              validityResult: toValidity(child.validityResult),
            };
          }

          if (child.__typename === 'PipelineGuardProjectHasTagCondition') {
            return {
              type: 'projectHasTag',
              tagPublicId: child.tag?.publicId ?? '',
              tagName: child.tag?.name ?? '',
              tagColour: child.tag?.colour ?? null,
              tempIdentifier: PLACEHOLDER,
              validityResult: toValidity(child.validityResult),
            };
          }

          if (child.__typename === 'PipelineGuardCustomFieldCondition') {
            const operator = predicateTypenameToOperator(
              child.predicate.__typename
            );
            return {
              type: 'customFieldCondition',
              fieldPath: child.fieldPath,
              operator,
              comparisonValue:
                operatorCarriesValue(operator) &&
                'comparisonValue' in child.predicate
                  ? child.predicate.comparisonValue
                  : undefined,
              resolvedField: child.field ?? null,
              tempIdentifier: PLACEHOLDER,
              validityResult: toValidity(child.validityResult),
            };
          }

          if (child.__typename === 'PipelineGuardNot' && child.child) {
            const innerChild = child.child;
            if (
              'type' in innerChild &&
              innerChild.__typename === 'PipelineGuardSimpleCondition'
            ) {
              const inner: PipelineGuardSimple = {
                type: 'simple',
                conditionType: innerChild.type,
                tempIdentifier: PLACEHOLDER,
                validityResult: toValidity(innerChild.validityResult),
              };
              return {
                type: 'not',
                child: inner,
                tempIdentifier: PLACEHOLDER,
                validityResult: toValidity(child.validityResult),
              };
            }
            if (
              'tag' in innerChild &&
              innerChild.__typename === 'PipelineGuardProjectHasTagCondition'
            ) {
              const inner: PipelineGuardProjectHasTag = {
                type: 'projectHasTag',
                tagPublicId: innerChild.tag?.publicId ?? '',
                tagName: innerChild.tag?.name ?? '',
                tagColour: innerChild.tag?.colour ?? null,
                tempIdentifier: PLACEHOLDER,
                validityResult: toValidity(innerChild.validityResult),
              };
              return {
                type: 'not',
                child: inner,
                tempIdentifier: PLACEHOLDER,
                validityResult: toValidity(child.validityResult),
              };
            }
          }

          return null;
        })
        .filter((c): c is PipelineGuardAndChild => c !== null);

      if (builtChildren.length === 0) {
        return null;
      }

      // Second pass: assign tempIdentifiers using pair-wise matching
      // against the previous group's children.
      const pairedIds = pairChildIdentifiers(
        builtChildren,
        previousAndGroup?.children
      );
      const useExactPositionPairing =
        !!previousAndGroup &&
        previousAndGroup.children.length === builtChildren.length;

      const children: PipelineGuardAndChild[] = builtChildren.map(
        (child, index) => {
          if (child.type !== 'not') {
            const pairedTempId = pairedIds[index] ?? uuid();
            return { ...child, tempIdentifier: pairedTempId };
          }
          // For `not`, allocate the inner id before the outer to keep
          // the historical uuid issuance order (inner first, then
          // outer) that test fixtures depend on.
          const positionPair = useExactPositionPairing
            ? previousAndGroup.children[index]
            : undefined;
          const innerPaired = pairInnerNotIdentifier(positionPair) ?? uuid();
          const pairedTempId = pairedIds[index] ?? uuid();
          return {
            ...child,
            child: { ...child.child, tempIdentifier: innerPaired },
            tempIdentifier: pairedTempId,
          };
        }
      );

      return {
        type: 'and' as const,
        children,
        tempIdentifier: previousAndGroup?.tempIdentifier ?? uuid(),
      };
    })
    .filter((g): g is PipelineGuardAnd => g !== null);

  if (andGroups.length === 0) {
    return null;
  }

  return {
    type: 'or',
    children: andGroups,
  };
}

export function formStateToGraphqlInput(
  formState: PipelineGuardOrFormState | null
): PipelineGuardInput | null {
  if (!formState || formState.children.length === 0) {
    return null;
  }

  const orChildren: PipelineGuardInput[] = formState.children.map(
    (andGroup) => {
      const andChildren: PipelineGuardInput[] = andGroup.children.map(
        (child) => {
          if (child.type === 'simple') {
            return {
              simpleCondition: { type: child.conditionType },
            };
          }

          if (child.type === 'projectHasTag') {
            return {
              projectHasTagCondition: { tagPublicId: child.tagPublicId },
            };
          }

          if (child.type === 'customFieldCondition') {
            return {
              customFieldCondition: {
                fieldPath: child.fieldPath,
                predicate: operatorToPredicateInput(
                  child.operator,
                  child.comparisonValue
                ),
              },
            };
          }

          // child.type === 'not' — `customFieldCondition` is forbidden under
          // `not`, so the inner child is one of the not-eligible variants.
          if (child.child.type === 'simple') {
            return {
              not: {
                simpleCondition: { type: child.child.conditionType },
              },
            };
          }

          // not > projectHasTag
          return {
            not: {
              projectHasTagCondition: {
                tagPublicId: child.child.tagPublicId,
              },
            },
          };
        }
      );

      return { and: andChildren };
    }
  );

  return { or: orChildren };
}

const DEFAULT_CONDITION_TYPE: PipelineGuardSimpleConditionType =
  'PROJECT_HAS_ACCEPTED_PROPOSAL';

export function createDefaultAndGuard(): PipelineGuardAnd {
  return {
    type: 'and',
    children: [
      {
        type: 'simple',
        conditionType: DEFAULT_CONDITION_TYPE,
        tempIdentifier: uuid(),
      },
    ],
    tempIdentifier: uuid(),
  };
}

export function createDefaultOrGuard(): PipelineGuardOrFormState {
  return {
    type: 'or',
    children: [createDefaultAndGuard()],
  };
}

export function guardInputToGraphqlResponse(
  input: PipelineGuardInput | null | undefined
): GraphQLGuardOr | null {
  if (!input || !input.or) {
    return null;
  }

  return {
    __typename: 'PipelineGuardOr' as const,
    validityResult: ALWAYS_VALID,
    children: input.or.map((andInput) => ({
      __typename: 'PipelineGuardAnd' as const,
      validityResult: ALWAYS_VALID,
      children:
        andInput.and
          ?.map((child) => {
            if (child.simpleCondition) {
              return {
                __typename: 'PipelineGuardSimpleCondition' as const,
                validityResult: ALWAYS_VALID,
                type: child.simpleCondition.type,
              };
            }
            if (child.projectHasTagCondition) {
              return {
                __typename: 'PipelineGuardProjectHasTagCondition' as const,
                validityResult: ALWAYS_VALID,
                tag: {
                  publicId: child.projectHasTagCondition.tagPublicId,
                  name: '',
                  colour: null,
                },
              };
            }
            if (child.customFieldCondition) {
              const cfc = child.customFieldCondition;
              const { operator, comparisonValue } = extractPredicateInput(
                cfc.predicate
              );
              return {
                __typename: 'PipelineGuardCustomFieldCondition' as const,
                validityResult: ALWAYS_VALID,
                fieldPath: cfc.fieldPath,
                predicate: buildPredicateResponse(operator, comparisonValue),
                // The optimistic response has no resolved field; the next
                // refetch fills it in from the server.
                field: null,
              };
            }
            if (child.not?.simpleCondition) {
              return {
                __typename: 'PipelineGuardNot' as const,
                validityResult: ALWAYS_VALID,
                child: {
                  __typename: 'PipelineGuardSimpleCondition' as const,
                  validityResult: ALWAYS_VALID,
                  type: child.not.simpleCondition.type,
                },
              };
            }
            if (child.not?.projectHasTagCondition) {
              return {
                __typename: 'PipelineGuardNot' as const,
                validityResult: ALWAYS_VALID,
                child: {
                  __typename: 'PipelineGuardProjectHasTagCondition' as const,
                  validityResult: ALWAYS_VALID,
                  tag: {
                    publicId: child.not.projectHasTagCondition.tagPublicId,
                    name: '',
                    colour: null,
                  },
                },
              };
            }
            return null;
          })
          .filter((c): c is NonNullable<typeof c> => c !== null) ?? [],
    })),
  } satisfies GraphQLGuardOr;
}
